import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardHeader,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import { getStoredToken } from "../utils/getStoredToken";
import { PartnerSafeApi } from './EnvFiles';
import { formatCPF } from '../utils/formatCPF';
import { validateCPFFormat } from '../utils/validateCPFFormat';
import { capitalizeText } from '../utils/capitalizeText';

const UsersPanel = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [formData, setFormData] = useState({
    primeiroNome: '',
    sobrenome: '',
    cpf: '',
    senha: '',
    confirmarSenha: '',
    des_centro_servico: [],
  });

  const [formErrors, setFormErrors] = useState({
    primeiroNome: false,
    sobrenome: false,
    cpf: false,
    senha: false,
    confirmarSenha: false,
  });
  
  const fetchMyData = useCallback(async () => {
    const token = getStoredToken();

    if (!token) return;

    setIsLoading(true);

    try {
      const response = await axios.get(
        `${PartnerSafeApi}/api/v1/Contas/MeusDados`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = response.data;

      const isAdmin = data.roles.some(
        (role) => ['Admin', 'BackOffice'].includes(role.nome),
      );

      if (!isAdmin) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      console.error('Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${PartnerSafeApi}/api/v1/Contas/MeusDados`,
      });

      setSnackbarMessage('Erro ao buscar dados do usuário');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchMyData();
  }, [fetchMyData]);

  const handleChange = (e) => {
    let { name, value } = e.target;


    if (['primeiroNome', 'sobrenome'].includes(name)) {
      value = capitalizeText(value);
    } else if (name === 'cpf') {
      value = formatCPF(value)
    }

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
  
    const errors = {
      primeiroNome: formData.primeiroNome.trim() === '',
      sobrenome: formData.sobrenome.trim() === '',
      cpf: formData.cpf === '' || !validateCPFFormat(formData.cpf),
      senha: formData.senha === '',
      confirmarSenha: formData.confirmarSenha === ''
        || formData.senha !== formData.confirmarSenha,
    };
  
    setFormErrors(errors);
  
    const hasError = Object.values(errors).some((e) => e);
  
    if (hasError) {
      setSnackbarMessage('Verifique os campos com erro destacados.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
  
    const userPayload = {
      primeiroNome: formData.primeiroNome,
      sobrenome: formData.sobrenome,
      cpf: formData.cpf,
      senha: formData.senha,
      des_centro_servico: formData.des_centro_servico,
    };
  
    try {
      setIsLoading(true);
  
      await axios.post(
        `${PartnerSafeApi}/api/v1/Contas/CriarUsuario`,
        userPayload,
      );
  
      setSnackbarMessage('Usuário criado com sucesso!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
  
      navigate('/users-panel/management');
    } catch (error) {
      console.error('Erro ao criar usuário:', error.response?.data);
  
      let errorMessage = error.response?.data?.error
        || 'Erro ao criar usuário. Tente novamente.';
  
      if (errorMessage.includes('Invalid CPF (Parameter \'value\')')) {
        errorMessage = 'CPF Inválido. Verifique e tente novamente.';
        setFormErrors((previous) => ({ ...previous, cpf: true }));
      }

      if (errorMessage.includes('Invalid password (Parameter \'value\')')) {
        errorMessage = 'Senha Inválida. Verifique e tente novamente.';
        setFormErrors((previous) => ({ ...previous, senha: true }));
      }
  
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFocus = (field) => {
    setFormErrors((previous) => ({ ...previous, [field]: false }));
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card>
        <CardHeader
          title={
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: '#40685E',
              }}
            >
              Criar Usuário
            </Typography>
          }
        />
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Primeiro Nome *"
              name="primeiroNome"
              value={formData.primeiroNome}
              onChange={handleChange}
              onFocus={() => handleFocus('primeiroNome')}
              error={formErrors.primeiroNome}
              helperText={formErrors.primeiroNome && 'Primeiro nome obrigatório'}
            />
            <TextField
              fullWidth
              label="Sobrenome *"
              name="sobrenome"
              value={formData.sobrenome}
              onChange={handleChange}
              onFocus={() => handleFocus('sobrenome')}
              error={formErrors.sobrenome}
              helperText={formErrors.sobrenome && 'Sobrenome obrigatório'}
            />
            <TextField
              fullWidth
              label="CPF *"
              name="cpf"
              value={formData.cpf}
              onChange={handleChange}
              onFocus={() => handleFocus('cpf')}
              error={formErrors.cpf}
              helperText={formErrors.cpf
                ? (formData.cpf.length ? 'CPF inválido' : 'CPF obrigatório')
                : ''}
            />
            <TextField
              fullWidth
              label="Senha *"
              name="senha"
              type="password"
              value={formData.senha}
              onChange={handleChange}
              onFocus={() => handleFocus('senha')}
              error={formErrors.senha}
              helperText={formErrors.senha && formData.senha.length
                ? 'No mínimo 8 caracteres com maiúscula, minúscula, número e símbolo.'
                : 'Campo obrigatório'
              }
            />
            <TextField
              fullWidth
              label="Confirmar Senha *"
              name="confirmarSenha"
              type="password"
              value={formData.confirmarSenha}
              onChange={handleChange}
              onFocus={() => handleFocus('confirmarSenha')}
              error={formErrors.confirmarSenha}
              helperText={formErrors.confirmarSenha
                && 'As senhas não coincidem ou campo está vazio'}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{ mt: 3 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : 'Criar'}
            </Button>
          </Box>
        </Box>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UsersPanel;